import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Activity } from "lucide-react";
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

/**
 * Increment B — grouped loaded squat-jump capture + validated Samozino F-v profile.
 * Records live inside the patient-specific Physical Tests workspace (one system).
 * The server performs the validated calculation; this component only previews and
 * reopens stored profiles. Loading conditions (kg) group multiple trials (cm);
 * multiple trials at one load are NOT separate conditions.
 */
const inp = "fl-input w-full text-sm px-2 py-2 rounded-md";
const num = (v) => (v === "" || v == null ? null : parseFloat(v));
const BLANK_CONDITIONS = () => ([
  { external_load_kg: "0", trials: [{ jump_height_cm: "" }] },
  { external_load_kg: "", trials: [{ jump_height_cm: "" }] },
  { external_load_kg: "", trials: [{ jump_height_cm: "" }] },
]);

export function FvGraph({ computed }) {
  const { f0_n: F0, v0_ms: V0, conditions = [] } = computed || {};
  const line = useMemo(() => {
    if (!F0 || !V0) return [];
    const out = [];
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const v = (V0 * i) / steps;
      out.push({ v: +v.toFixed(3), F: +(F0 * (1 - v / V0)).toFixed(1) });
    }
    return out;
  }, [F0, V0]);
  const points = (conditions || [])
    .filter((c) => c.vmean_ms != null && c.fmean_n != null)
    .map((c) => ({ v: c.vmean_ms, F: c.fmean_n, load: c.external_load_kg }));
  const maxV = useMemo(() => (V0 ? Math.ceil(V0 * 1.05 * 2) / 2 : 0), [V0]); // nice round to 0.5 m/s
  const xTicks = useMemo(() => Array.from({ length: Math.floor(maxV) + 1 }, (_, i) => i), [maxV]);
  if (!F0 || !V0) return null;
  return (
    <div className="h-72 w-full" data-testid="fv-graph">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart margin={{ top: 10, right: 20, bottom: 24, left: 8 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.08)" />
          <XAxis type="number" dataKey="v" domain={[0, +maxV.toFixed(2)]} ticks={xTicks}
            tick={{ fill: "#a1a1aa", fontSize: 11 }}
            label={{ value: "Velocity (m/s)", position: "insideBottom", offset: -12, fill: "#a1a1aa", fontSize: 11 }} />
          <YAxis type="number" tick={{ fill: "#a1a1aa", fontSize: 11 }}
            label={{ value: "Force (N)", angle: -90, position: "insideLeft", fill: "#a1a1aa", fontSize: 11 }} />
          <Tooltip contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
            formatter={(val, name) => [val, name === "F" ? "Force (N)" : name]} labelFormatter={(v) => `v = ${v} m/s`} />
          <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11, paddingBottom: 6 }} />
          <Line data={line} dataKey="F" name="Theoretical F–v" stroke="#f47a20" dot={false} strokeWidth={2} isAnimationActive={false} />
          <Scatter data={points} dataKey="F" name="Measured conditions" fill="#38bdf8" isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// Increment C — overlay two or more stored F–v profiles on ONE graph.
// Each theoretical line is extrapolated to its own F0/V0 intercepts; measured
// condition points are plotted in the matching colour. No new model is fitted.
export function FvOverlayGraph({ series }) {
  const valid = (series || []).filter((s) => s?.computed?.f0_n && s?.computed?.v0_ms);
  const rawV = valid.length ? Math.max(...valid.map((s) => s.computed.v0_ms)) * 1.05 : 0;
  const rawF = valid.length ? Math.max(...valid.map((s) => s.computed.f0_n)) * 1.05 : 0;
  const maxV = rawV ? Math.ceil(rawV * 2) / 2 : 0;      // nice round to 0.5 m/s
  const maxF = rawF ? Math.ceil(rawF / 100) * 100 : 0;   // nice round to 100 N
  const xTicks = Array.from({ length: Math.floor(maxV) + 1 }, (_, i) => i);
  const mkLine = (c) => {
    const out = [];
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const v = (c.v0_ms * i) / steps;
      out.push({ v: +v.toFixed(3), F: +(c.f0_n * (1 - v / c.v0_ms)).toFixed(1) });
    }
    return out;
  };
  const mkPoints = (c) => (c.conditions || [])
    .filter((x) => x.vmean_ms != null && x.fmean_n != null)
    .map((x) => ({ v: x.vmean_ms, F: x.fmean_n }));
  if (!valid.length) return null;
  return (
    <div className="h-72 w-full" data-testid="fv-overlay-graph">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart margin={{ top: 10, right: 20, bottom: 24, left: 8 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.08)" />
          <XAxis type="number" dataKey="v" domain={[0, +maxV.toFixed(2)]} ticks={xTicks}
            tick={{ fill: "#a1a1aa", fontSize: 11 }}
            label={{ value: "Velocity (m/s)", position: "insideBottom", offset: -12, fill: "#a1a1aa", fontSize: 11 }} />
          <YAxis type="number" domain={[0, +maxF.toFixed(0)]} tick={{ fill: "#a1a1aa", fontSize: 11 }}
            label={{ value: "Force (N)", angle: -90, position: "insideLeft", fill: "#a1a1aa", fontSize: 11 }} />
          <Tooltip contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
            formatter={(val, name) => [val, name]} labelFormatter={(v) => `v = ${v} m/s`} />
          <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11, paddingBottom: 6 }} />
          {valid.map((s, i) => (
            <Line key={`l${i}`} data={mkLine(s.computed)} dataKey="F" name={s.label}
              stroke={s.color} dot={false} strokeWidth={2} isAnimationActive={false} />
          ))}
          {valid.map((s, i) => (
            <Scatter key={`p${i}`} data={mkPoints(s.computed)} dataKey="F" name={`${s.label} · points`}
              fill={s.color} isAnimationActive={false} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProfileSummary({ computed }) {
  if (!computed) return null;
  if (!computed.valid) {
    return (
      <div className="text-sm text-amber-400 mt-2" data-testid="fv-insufficient">
        Insufficient compatible data to calculate a valid force–velocity profile.
        <ul className="list-disc ml-5 mt-1 text-amber-300/80">
          {(computed.errors || []).map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      </div>
    );
  }
  const stat = (label, val, unit) => (
    <div className="text-center"><div className="fl-label">{label}</div><div className="fl-heading text-lg text-[#f47a20]">{val}<span className="text-xs text-zinc-400"> {unit}</span></div></div>
  );
  return (
    <div data-testid="fv-summary">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        {stat("F0", computed.f0_n, "N")}
        {stat("V0", computed.v0_ms, "m/s")}
        {stat("Pmax", computed.pmax_w, "W")}
        {stat("F–v slope", computed.sfv_n_per_ms, "N/(m/s)")}
        {stat("R²", computed.r2, "")}
      </div>
      <FvGraph computed={computed} />
      {(computed.warnings || []).map((w, i) => <div key={i} className="text-[11px] text-amber-400/80 mt-1">{w}</div>)}
      <div className="text-[11px] text-zinc-500 mt-1">Samozino squat-jump field method (2008). Model-derived theoretical maxima — not direct force-plate measurements. Pmax = F0·V0/4.</div>
    </div>
  );
}

export default function FvAssessment({ patientId, onSaved }) {
  const [hdr, setHdr] = useState({
    body_mass_kg: "", push_off_cm: "", leg_length_cm: "", squat_height_cm: "",
    representative: "best", performed_at: new Date().toISOString().slice(0, 10), notes: "", released: false,
  });
  const [conditions, setConditions] = useState(BLANK_CONDITIONS);
  const [busy, setBusy] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    try { const r = await api.get(`/physical-tests/fv-profiles/patient/${patientId}`); setProfiles(r.data || []); }
    catch { /* non-blocking */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const setH = (k, v) => setHdr((s) => ({ ...s, [k]: v }));
  const addCondition = () => setConditions((cs) => [...cs, { external_load_kg: "", trials: [{ jump_height_cm: "" }] }]);
  const removeCondition = (i) => setConditions((cs) => cs.filter((_, idx) => idx !== i));
  const setLoad = (i, v) => setConditions((cs) => cs.map((c, idx) => idx === i ? { ...c, external_load_kg: v } : c));
  const addTrial = (i) => setConditions((cs) => cs.map((c, idx) => idx === i ? { ...c, trials: [...c.trials, { jump_height_cm: "" }] } : c));
  const removeTrial = (i, j) => setConditions((cs) => cs.map((c, idx) => idx === i ? { ...c, trials: c.trials.filter((_, t) => t !== j) } : c));
  const setTrial = (i, j, v) => setConditions((cs) => cs.map((c, idx) => idx === i ? { ...c, trials: c.trials.map((t, tj) => tj === j ? { ...t, jump_height_cm: v } : t) } : c));

  const bestMean = (trials) => {
    const hs = trials.map((t) => num(t.jump_height_cm)).filter((h) => h != null && h >= 0);
    if (!hs.length) return null;
    return { best: Math.max(...hs), mean: +(hs.reduce((a, b) => a + b, 0) / hs.length).toFixed(1) };
  };

  // Required to save: body mass, push-off and at least one condition that has
  // both a load and a trial height (a lone height with no load is dropped server-side).
  const canSave = useMemo(() => {
    const hasMass = num(hdr.body_mass_kg) != null;
    const hasPush = num(hdr.push_off_cm) != null;
    const hasCompleteCond = conditions.some(
      (c) => num(c.external_load_kg) != null && c.trials.some((t) => num(t.jump_height_cm) != null)
    );
    return hasMass && hasPush && hasCompleteCond;
  }, [hdr.body_mass_kg, hdr.push_off_cm, conditions]);

  const save = async () => {
    if (busy || !canSave) return;   // guard against blank/incomplete or repeated submissions
    setBusy(true);
    try {
      const payload = {
        patient_id: patientId,
        performed_at: new Date(hdr.performed_at).toISOString(),
        body_mass_kg: num(hdr.body_mass_kg), push_off_cm: num(hdr.push_off_cm),
        leg_length_cm: num(hdr.leg_length_cm), squat_height_cm: num(hdr.squat_height_cm),
        representative: hdr.representative, notes: hdr.notes || null, released: hdr.released,
        conditions: conditions
          .filter((c) => num(c.external_load_kg) != null)
          .map((c) => ({
            external_load_kg: num(c.external_load_kg),
            trials: c.trials.filter((t) => num(t.jump_height_cm) != null)
              .map((t, k) => ({ trial_no: k + 1, jump_height_cm: num(t.jump_height_cm), valid: true })),
          })),
      };
      const r = await api.post("/physical-tests/fv-profiles", payload);
      if (r.data.computed?.valid) toast.success("Force–velocity profile calculated and saved.");
      else toast.message("Assessment saved — add the missing measurements to complete the profile.");
      setActive(r.data);          // keep the saved profile + graph visible
      await load();
      // Clear the new-assessment input state ONLY on success so an accidental
      // second click can't create a duplicate. The saved result stays on screen.
      setConditions(BLANK_CONDITIONS());
      setHdr((h) => ({ ...h, body_mass_kg: "", push_off_cm: "", leg_length_cm: "", squat_height_cm: "", notes: "", performed_at: new Date().toISOString().slice(0, 10) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this force–velocity assessment? This cannot be undone.")) return;
    try {
      await api.delete(`/physical-tests/fv-profiles/${id}`);
      toast.success("Assessment deleted.");
      if (active?.id === id) setActive(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="fv-assessment">
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <div><div className="fl-label mb-1">Body mass (kg) *</div><input type="number" step="any" className={inp} value={hdr.body_mass_kg} onChange={(e) => setH("body_mass_kg", e.target.value)} data-testid="fv-bodymass" /></div>
        <div><div className="fl-label mb-1">Push-off distance (cm) *</div><input type="number" step="any" className={inp} value={hdr.push_off_cm} onChange={(e) => setH("push_off_cm", e.target.value)} data-testid="fv-pushoff" /></div>
        <div><div className="fl-label mb-1">Representative</div>
          <select className={inp} value={hdr.representative} onChange={(e) => setH("representative", e.target.value)} data-testid="fv-representative">
            <option value="best">Best valid trial</option><option value="mean">Mean of valid trials</option>
          </select>
        </div>
        <div><div className="fl-label mb-1">Date</div><input type="date" className={inp} value={hdr.performed_at} onChange={(e) => setH("performed_at", e.target.value)} data-testid="fv-date" /></div>
        <div><div className="fl-label mb-1">Leg length (cm)</div><input type="number" step="any" className={inp} value={hdr.leg_length_cm} onChange={(e) => setH("leg_length_cm", e.target.value)} data-testid="fv-leglength" /></div>
        <div><div className="fl-label mb-1">Squat/hip height (cm)</div><input type="number" step="any" className={inp} value={hdr.squat_height_cm} onChange={(e) => setH("squat_height_cm", e.target.value)} data-testid="fv-squatheight" /></div>
      </div>
      <div className="text-[11px] text-zinc-500 mb-3">Squat-jump protocol only. Enter measurements in cm/kg — conversions are internal. No push-off default is assumed. At least 3 distinct valid loads are required.</div>

      <div className="space-y-3" data-testid="fv-conditions">
        {conditions.map((c, i) => {
          const bm = bestMean(c.trials);
          return (
            <div key={i} className="fl-card p-3 border border-white/10" data-testid={`fv-condition-${i}`}>
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="fl-label">Loading condition {i + 1}</span>
                  <input type="number" step="any" placeholder="load" className="fl-input w-24 text-sm px-2 py-1 rounded-md" value={c.external_load_kg} onChange={(e) => setLoad(i, e.target.value)} data-testid={`fv-load-${i}`} />
                  <span className="text-xs text-zinc-400">kg</span>
                </div>
                <div className="flex items-center gap-3">
                  {bm && <span className="text-[11px] text-zinc-400">best {bm.best} · mean {bm.mean} cm</span>}
                  {conditions.length > 1 && <button type="button" onClick={() => removeCondition(i)} className="text-zinc-500 hover:text-red-400" title="Remove condition" data-testid={`fv-remove-load-${i}`}><Trash2 size={14} /></button>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {c.trials.map((t, j) => (
                  <div key={j} className="flex items-center gap-1">
                    <span className="text-[10px] text-zinc-500">#{j + 1}</span>
                    <input type="number" step="any" placeholder="cm" className="fl-input w-20 text-sm px-2 py-1 rounded-md" value={t.jump_height_cm} onChange={(e) => setTrial(i, j, e.target.value)} data-testid={`fv-trial-${i}-${j}`} />
                    {c.trials.length > 1 && <button type="button" onClick={() => removeTrial(i, j)} className="text-zinc-600 hover:text-red-400" data-testid={`fv-remove-trial-${i}-${j}`}>×</button>}
                  </div>
                ))}
                <button type="button" onClick={() => addTrial(i)} className="fl-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`fv-add-trial-${i}`}><Plus size={12} /> trial</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex gap-2 flex-wrap">
        <button type="button" onClick={addCondition} className="fl-btn-ghost text-sm inline-flex items-center gap-1" data-testid="fv-add-condition"><Plus size={14} /> Add loading condition</button>
        <button type="button" onClick={save} disabled={busy || !canSave} className="fl-btn text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="fv-save"><Activity size={14} /> {busy ? "Calculating…" : "Save & calculate profile"}</button>
      </div>

      {active && (
        <div className="fl-card p-4 mt-4 border border-[#f47a20]/30" data-testid="fv-result">
          <div className="fl-label mb-2">Result · {(active.performed_at || "").slice(0, 10)}</div>
          <ProfileSummary computed={active.computed} />
        </div>
      )}

      {profiles.length > 0 && (
        <div className="mt-4" data-testid="fv-history">
          <div className="fl-label mb-2">Saved force–velocity assessments</div>
          <div className="flex flex-wrap gap-2">
            {profiles.map((p) => (
              <div key={p.id} className="inline-flex items-center rounded-full border border-white/10 overflow-hidden">
                <button type="button" onClick={() => setActive(p)}
                  className="text-xs px-3 py-1.5 text-zinc-300 hover:text-[#f47a20]"
                  data-testid={`fv-reopen-${p.id.slice(0, 8)}`}>
                  {(p.performed_at || "").slice(0, 10)} · {p.computed?.valid ? `Pmax ${p.computed.pmax_w} W` : "incomplete"}
                </button>
                <button type="button" onClick={() => del(p.id)} title="Delete assessment"
                  className="px-2 py-1.5 text-zinc-500 hover:text-red-400 border-l border-white/10"
                  data-testid={`fv-delete-${p.id.slice(0, 8)}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
